import React, { useEffect, useMemo, useState } from 'react';
import { geoMercator, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import { COUCHES, ZONES, couleurType } from './couches';

let fondCache = null;
const chargerFond = () => {
  if (!fondCache) fondCache = import('world-atlas/countries-50m.json').then((m) => feature(m.default || m, (m.default || m).objects.countries));
  return fondCache;
};

const STYLE = `
@keyframes cv-pulse { 0% { r: var(--r0); opacity: .55 } 100% { r: calc(var(--r0) * 2.3); opacity: 0 } }
@keyframes cv-pop { 0% { transform: scale(0) } 70% { transform: scale(1.15) } 100% { transform: scale(1) } }
@keyframes cv-fade { from { opacity: 0 } to { opacity: 1 } }
.cv-halo { animation: cv-pulse 2.4s ease-out infinite; }
.cv-pin { transform-box: fill-box; transform-origin: center; animation: cv-pop .6s ease-out both; cursor: pointer; }
.cv-pin:hover circle.cv-coeur { stroke-width: 3; }
.cv-fond { animation: cv-fade .5s ease-out both; }
`;

const enRect = ([o, s, e, n]) => ({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [[[o, s], [e, s], [e, n], [o, n], [o, s]]] } });
const dansZone = ([lat, lng], [o, s, e, n]) => lng >= o && lng <= e && lat >= s && lat <= n;

const arc = (cx, cy, r, a0, a1) => {
  const p = (a) => [cx + r * Math.sin(a), cy - r * Math.cos(a)];
  const [x0, y0] = p(a0); const [x1, y1] = p(a1);
  return `M ${x0} ${y0} A ${r} ${r} 0 ${a1 - a0 > Math.PI ? 1 : 0} 1 ${x1} ${y1}`;
};

function Anneau({ cx, cy, r, types }) {
  const entrees = Object.entries(types || {});
  const total = entrees.reduce((s, [, v]) => s + v, 0);
  if (!total) return null;
  let a = 0;
  return entrees.map(([t, v]) => {
    const part = (v / total) * Math.PI * 2;
    const d = arc(cx, cy, r + 4, a, a + part - (entrees.length > 1 ? 0.06 : 0.0001));
    a += part;
    return <path key={t} d={d} fill="none" stroke={couleurType(t)} strokeWidth="4" strokeLinecap="round" />;
  });
}

function Encart({ zone, fond, territoires, visibles, largeur, hauteur, onSurvol, onChoisir, titre }) {
  const projection = useMemo(() => geoMercator().fitExtent([[8, titre ? 26 : 8], [largeur - 8, hauteur - 8]], enRect(zone.bbox)), [zone, largeur, hauteur, titre]);
  const chemin = useMemo(() => geoPath(projection), [projection]);
  const ici = territoires.filter((t) => t.coords && dansZone(t.coords, zone.bbox));
  return (
    <svg viewBox={`0 0 ${largeur} ${hauteur}`} className="h-full w-full" data-testid={`carte-encart-${zone.nom}`}>
      <defs><clipPath id={`clip-${zone.nom}`}><rect width={largeur} height={hauteur} rx="14" /></clipPath></defs>
      <g clipPath={`url(#clip-${zone.nom})`}>
        <rect width={largeur} height={hauteur} fill="#0b1220" />
        <g className="cv-fond">
          {fond?.features.map((f, i) => <path key={i} d={chemin(f) || ''} fill="#1e293b" stroke="#334155" strokeWidth="0.6" />)}
        </g>
        {ici.flatMap((t, ti) => {
          const [x, y] = projection([t.coords[1], t.coords[0]]);
          const pins = visibles.filter((c) => t[c.id] > 0);
          return pins.map((c, ci) => {
            const r = Math.round(11 + Math.min(17, Math.sqrt(t[c.id]) * 2.6));
            const a = (c.angle * Math.PI) / 180;
            const d = pins.length > 1 ? r + 4 : 0;
            const px = x + d * Math.cos(a); const py = y - d * Math.sin(a);
            return (
              <g key={`${t.territoire}-${c.id}`} className="cv-pin" style={{ animationDelay: `${(ti * 4 + ci) * 70}ms` }}
                onMouseEnter={(e) => onSurvol({ t, x: e.clientX, y: e.clientY })} onMouseMove={(e) => onSurvol({ t, x: e.clientX, y: e.clientY })}
                onMouseLeave={() => onSurvol(null)} onClick={() => onChoisir?.(t)} data-testid={`carte-pin-${t.territoire}-${c.id}`}>
                <circle className="cv-halo" cx={px} cy={py} r={r} fill={c.couleur} style={{ '--r0': `${r}px` }} />
                <circle className="cv-coeur" cx={px} cy={py} r={r} fill={c.couleur} stroke="#fff" strokeOpacity=".85" strokeWidth="2" />
                {c.id === 'dispositifs' && <Anneau cx={px} cy={py} r={r} types={t.types} />}
                <text x={px} y={py} dy="0.35em" textAnchor="middle" fill="#fff" fontSize={r > 16 ? 12 : 10} fontWeight="700">{t[c.id]}</text>
              </g>
            );
          });
        })}
        {titre && <text x="12" y="18" fill="#94a3b8" fontSize="11" fontWeight="700" letterSpacing=".08em">{zone.nom.toUpperCase()}</text>}
      </g>
    </svg>
  );
}

function Fiche({ survol, visibles }) {
  if (!survol) return null;
  const { t, x, y } = survol;
  return (
    <div className="pointer-events-none fixed z-50 min-w-[190px] rounded-xl border border-slate-700 bg-slate-900/95 px-3 py-2 text-slate-200 shadow-2xl backdrop-blur"
      style={{ left: x + 14, top: y + 14 }} data-testid="carte-fiche-survol">
      <p className="mb-1 text-sm font-bold">{t.territoire}</p>
      {visibles.map((c) => (
        <p key={c.id} className="flex justify-between gap-4 text-xs"><span><span className="mr-1.5 inline-block h-2 w-2 rounded-full" style={{ background: c.couleur }} />{c.court}</span><b>{t[c.id] || 0}</b></p>
      ))}
      {t.types && Object.keys(t.types).length > 0 && (
        <div className="mt-1.5 border-t border-slate-700 pt-1.5">
          {Object.entries(t.types).map(([k, v]) => <p key={k} className="flex justify-between gap-4 text-[11px] text-slate-300"><span><span className="mr-1.5 inline-block h-1.5 w-3 rounded-full" style={{ background: couleurType(k) }} />{k}</span><b>{v}</b></p>)}
        </div>
      )}
      {survol.cliquable && <p className="mt-1 text-[10px] text-slate-500">Cliquer pour ouvrir la fiche</p>}
      {t.travaux_liste?.length > 0 && <p className="mt-1 text-[11px] text-slate-400">Chantiers : {t.travaux_liste.slice(0, 3).map((w) => w.ville).filter(Boolean).join(', ')}</p>}
    </div>
  );
}

export default function CarteVivanteMap({ territoires, actives, cible, hauteur = 520, onChoisir, refCarte }) {
  const [fond, setFond] = useState(null);
  const [survol, setSurvolBrut] = useState(null);
  const setSurvol = (s) => setSurvolBrut(s && { ...s, cliquable: !!onChoisir });
  useEffect(() => { chargerFond().then(setFond); }, []);
  const visibles = COUCHES.filter((c) => actives.includes(c.id));
  const commun = { fond, territoires, visibles, onSurvol: setSurvol, onChoisir };
  const zone = cible?.bbox ? cible : null;
  return (
    <div ref={refCarte} className="relative overflow-hidden rounded-2xl bg-[#0b1220]" style={{ height: hauteur }} data-testid="carte-vivante-map">
      <style>{STYLE}</style>
      {zone ? (
        <Encart key={zone.nom} zone={zone} largeur={1000} hauteur={hauteur} {...commun} />
      ) : (
        <div className="grid h-full grid-cols-6 grid-rows-2 gap-2 p-2">
          {ZONES.map((z, i) => (
            <div key={z.nom} className={`overflow-hidden rounded-[14px] ring-1 ring-white/5 ${i < 2 ? 'col-span-3' : 'col-span-2'}`}>
              <Encart zone={z} largeur={i < 2 ? 500 : 330} hauteur={Math.round(hauteur / 2) - 8} titre {...commun} />
            </div>
          ))}
        </div>
      )}
      <Fiche survol={survol} visibles={visibles} />
      <p className="absolute bottom-1.5 right-3 text-[10px] text-slate-600">Contours : Natural Earth (domaine public)</p>
    </div>
  );
}
